import * as React from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"

import { AppStore } from "../../store"
import OldUIContext from "../../olduicontext"
import { IOldUIContext } from "../../global"

import { ISuggestedAction } from "."
import * as AISlice from "./slice"
import AIModal from "./components/AIModal"

interface OldUIInit {
    start: (
        elementSelector: string,
        props: OldUIProps,
        fetchSuggestedActions: () => void,
        addSuggestedActions: (suggestedActions: ISuggestedAction[]) => void,
    ) => void
    addMessage: (id: string, message: string, isSentByUser: boolean) => void
    setIsLoading: (id: string, isLoading: boolean) => void
    setIsVisible: (id: string, isVisible: boolean) => void
    receiveSuggestedActions: (
        id: string,
        suggestedActions: ISuggestedAction[],
    ) => void
    removeSuggestedActions: (
        id: string,
        suggestedActions: ISuggestedAction[],
    ) => void
}

type OldUIProps = AISlice.Initialize

function init(store: AppStore, oldUIContext: IOldUIContext): OldUIInit {
    const start = (
        elementSelector: string,
        props: OldUIProps,
        fetchSuggestedActions: () => void,
        addSuggestedActions: (suggestedActions: ISuggestedAction[]) => void,
    ): void => {
        // we cannot pass down an element, because this is called from an iframe custom view
        // but the container we should load into is not within an iframe
        const element = document.querySelector(elementSelector)
        if (!element) {
            // target container doesn't exist, skip initialization
            return
        }

        store.dispatch(AISlice.initialize(props))

        const root = createRoot(element)
        root.render(
            <Provider store={store}>
                <OldUIContext.Provider value={oldUIContext}>
                    <AIModal
                        fetchSuggestedActions={fetchSuggestedActions}
                        addSuggestedActions={addSuggestedActions}
                        id={props.id}
                    />
                </OldUIContext.Provider>
            </Provider>,
        )
    }

    const addMessage = (
        id: string,
        message: string,
        isSentByUser: boolean,
    ): void => {
        store.dispatch(
            AISlice.addMessage({
                id,
                message,
                isSentByUser,
            }),
        )
    }

    const setIsLoading = (id: string, value: boolean): void => {
        store.dispatch(AISlice.setIsLoading({ id, value }))
    }

    const setIsVisible = (id: string, value: boolean): void => {
        store.dispatch(AISlice.setIsVisible({ id, value }))
    }

    const receiveSuggestedActions = (
        id: string,
        actions: ISuggestedAction[],
    ): void => {
        store.dispatch(AISlice.receiveSuggestedActions({ id, actions }))
    }

    const removeSuggestedActions = (
        id: string,
        actions: ISuggestedAction[],
    ): void => {
        store.dispatch(AISlice.removeSuggestedActions({ id, actions }))
    }

    return {
        start,
        addMessage,
        setIsLoading,
        setIsVisible,
        receiveSuggestedActions,
        removeSuggestedActions,
    }
}

export default init
